import { diff } from 'deep-object-diff'
import { TFunction } from 'i18next'
import { clamp, isNil } from 'lodash-es'
import memoizeOne from 'memoize-one'
import moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import { stringify } from 'qs'
import React from 'react'
import { PartialDeep } from 'type-fest'

import { headerHeight } from '@/_vars'
import { capitalize } from '@/lib/capitalize'
import {
	BindableLabel,
	CabinClassCodeLabels,
	CabinClassLabels,
	CabinClassName,
	GeoPoint,
	Segment,
	SelectOption,
} from '@/travelsuit'
import { Callback } from '@/travelsuit/callback'
import { ComparisonFn, ProductType, SortingFn, UserCurrency } from '@/types/common'

import { range } from './array-utils'
import { isIOS } from './browser'
import { getPriceLabel } from './getPriceLabel'
import { noop } from './noop'
import { Maybe } from './types'

export { range, noop }
momentDurationFormatSetup(moment)

export const MINUTE_IN_MILLISECONDS = 60 * 1000
export const HOUR_IN_MILLISECONDS = 60 * MINUTE_IN_MILLISECONDS

export const EMPTY_ARRAY = Object.freeze([] as unknown[]) as unknown[]

/**
 * This function takes functions that receive callbacks as the last parameter
 * and causes them to behave like a Promise, allowing them to be chained
 * @param fn function to wrap
 * @param args... extra arguments that will be passed to the function when it fires
 */

// eslint-disable-next-line no-restricted-syntax
enum validationFieldName {
	postal_code = 'postal_code',
	tax_number = 'tax_number',
}

type ValidationFieldName = keyof typeof validationFieldName

interface ValidationConstraint {
	regExp: RegExp
	validationMessage: string
}

type ValidationFields = {
	[key in ValidationFieldName]: ValidationConstraint
}

export function smartJoin(strs: string[], limit = 0, othersExpression = ' more', separator = ' and ') {
	if (strs.length < 2) {
		return strs.join(separator).trim()
	}

	const diff = strs.length - limit
	if (limit > 0 && diff > 0) {
		return (strs.slice(Math.min(strs.length, limit)).join(', ') + separator + diff + othersExpression).trim()
	}

	return (strs.slice(0, -1).join(', ') + separator + strs.slice(-1)).trim()
}

function splitOnce(string: string, separator = ''): string[] {
	const components = string.split(separator)
	return [components.shift()!, components.join(separator)]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends Function = (...a: any[]) => void>(cb: T, delay: number) {
	let timeout: number | undefined

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const debouncedCb = ((...args: any[]) =>
		new Promise((resolve) => {
			clear()

			timeout = window.setTimeout(() => {
				resolve(cb(...args))
			}, delay)
		})) as unknown as T

	function clear() {
		if (timeout) {
			clearTimeout(timeout)
		}
	}

	return { debouncedCb, clear }
}

export function objDiff<T = object>(obj1: Partial<T> = {}, obj2: Partial<T> = {}): Partial<T> {
	return diff(obj1, obj2)
}

export function pick<T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
	return keys.reduce(
		(slice, k) => ({
			...slice,
			[k]: obj[k],
		}),
		{} as unknown as Pick<T, K>,
	)
}

export function exclude<T, K extends string | number | symbol>(
	obj: Exclude<T, undefined | null> | null | undefined,
	keys: K[],
): Omit<T, K> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	if ([null, undefined].includes(obj as any)) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return obj as any
	}
	const source = obj as Record<string, T[keyof T]>
	const filtered = Object.keys(source).reduce<Partial<Omit<T, K>>>(
		(slice, k) => (keys.includes(k as K) ? slice : { ...slice, [k]: source[k] }),
		{},
	)
	return filtered as Omit<T, K>
}

const NON_ALPHA = /[^a-z]/gi
const NON_NUMERIC = /[^0-9]/g

/** @deprecated Use `(a, b) => a.localeCompare(b, 'en', { numeric: true })` instead */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortAlphaNum<T = any>(map?: (a: T) => string): SortingFn<T> {
	return (a: T, b: T) => {
		const mappedA = String(map ? map(a) : a)
		const mappedB = String(map ? map(b) : b)
		const alphaA = mappedA.replace(NON_ALPHA, '')
		const alphaB = mappedB.replace(NON_ALPHA, '')

		if (alphaA === alphaB) {
			const numericA = Number(mappedA.replace(NON_NUMERIC, ''))
			const numericB = Number(mappedB.replace(NON_NUMERIC, ''))
			return numericA === numericB ? 0 : numericA > numericB ? 1 : -1
		} else {
			return alphaA > alphaB ? 1 : -1
		}
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortGetter<O = any>(key: string): SortingFn<O>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortGetter<O = any>(key: string, a: O, b: O, defaultValue?: number): number
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortGetter<O = any>(key: string, a?: O, b?: O, def?: number): number | SortingFn<O> {
	if (a !== undefined && b !== undefined) {
		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		return (a[key] || def) - (b[key] || def)
	}
	// @ts-expect-error todo if you see this please remove this comment and fix the type error
	return (_a, _b) => _a[key] - _b[key]
}

export function geoDistanceKm(point1: GeoPoint, point2: GeoPoint): number {
	//taken from https://www.movable-type.co.uk/scripts/latlong.html
	const { latitude: lat1, longitude: lon1 } = point1
	const { latitude: lat2, longitude: lon2 } = point2

	const R = 6371e3
	const phi1 = (lat1 * Math.PI) / 180
	const phi2 = (lat2 * Math.PI) / 180
	const deltaPhi = ((lat2 - lat1) * Math.PI) / 180
	const deltaLambda = ((lon2 - lon1) * Math.PI) / 180

	const a =
		Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
		Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2)
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

	return (R * c) / 1000
}

interface TSScrollToOptions {
	x?: number
	y?: number
	scrollBehavior?: ScrollBehavior
	scrollOffsetX?: number
	scrollOffsetY?: number
	scrollElement?: HTMLElement
}

export function scrollTo({
	x,
	y,
	scrollBehavior = 'smooth',
	scrollOffsetY = headerHeight,
	scrollOffsetX = 0,
	scrollElement,
}: TSScrollToOptions) {
	const conf: ScrollToOptions = {
		behavior: scrollBehavior,
	}

	if (x !== undefined) {
		conf.left = x - scrollOffsetX
	}

	if (y !== undefined) {
		conf.top = y - scrollOffsetY
	}

	if (scrollElement) {
		scrollElement.scrollTo(conf)
	} else {
		window.scrollTo(conf)
	}
}

export type MapDelegate<T, R> = (k: T, i: number) => R

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSelectOption(arg: any): arg is SelectOption {
	return arg.value !== undefined && arg.label !== undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isSelectOptionsArray(arr: any[] = []): arr is SelectOption[] {
	return arr.every(isSelectOption)
}

export function mapSelectOptions<T, R = T, L = React.ReactNode>(
	arr: readonly T[] = [],
	{ mapLabel, mapValue }: { mapLabel?: MapDelegate<T, L>; mapValue?: MapDelegate<T, R> } = {},
): Array<SelectOption<R, L>> {
	return (arr as T[]).map((t, i) => mapSelectOption(t, i, { mapLabel, mapValue }))
}

function mapSelectOption<T, R = T, L = React.ReactNode>(
	t: T,
	i: number,
	{ mapLabel, mapValue }: { mapLabel?: MapDelegate<T, L>; mapValue?: MapDelegate<T, R> } = {},
): SelectOption<R, L> {
	return {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		label: (mapLabel ?? (((o: any) => String(o)) as unknown as MapDelegate<T, L>))(t, i),
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		value: (mapValue ?? (noop as any))(t, i),
	}
}

export function resolveBindableLabel<T, L extends BindableLabel<T>>(thisArg: T, label: L): React.ReactNode {
	return typeof label === 'function' ? label.call(thisArg) : label
}

export function getDuration(from: Date | string, to: Date | string) {
	const fromTime = new Date(from).getTime()
	const toTime = new Date(to).getTime()

	const millisecondsDiff = fromTime > toTime ? fromTime - toTime : toTime - fromTime

	return moment.duration({ milliseconds: millisecondsDiff })
}

export const getDurationFormat = (t: TFunction) => ({
	Short: {
		/**	En key: `h[h] m[m] s[s]`, En result: `7h 45m 3s` */
		HoursToSeconds: t('duration-formats.hours-to-seconds'),
		/**	En key: `w[w] d[d] h[h] m[m]`, En result: `3w 2d 7h 45m 3s` */
		WeeksToMinutes: t('duration-formats.weeks-to-minutes'),
		/**	En key: `w[w] d[d]`, En result: `3w 2d` */
		WeeksToDays: t('duration-formats.weeks-to-days'),
	},
})

export function getHumanizedDuration(t: TFunction, from: Date | string, to: Date | string, format?: string) {
	const duration = getDuration(from, to)
	return humanizedDurationLabel(t, duration, format ?? getDurationFormat(t).Short.WeeksToMinutes)
}

export function humanizedDurationLabel(
	t: TFunction,
	duration: moment.Duration | moment.DurationInputArg1,
	format?: string,
) {
	return (moment.isDuration(duration) ? duration : moment.duration(duration))
		.format(format ?? getDurationFormat(t).Short.WeeksToMinutes)
		.replace(/\b0\s?[wdhmsy][a-z]*\b/g, '')
}

export function cabinClassNameAndLetter(
	cabinClass: Segment['cabin_class'],
	brandName?: string,
	letter?: string,
): { cabinClass: keyof ReturnType<typeof getCabinClassNameTranslations>; letter?: string } {
	const name = getCabinClassName(cabinClass)

	if (!letter) {
		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		return { cabinClass: name }
	}

	// @ts-expect-error todo if you see this please remove this comment and fix the type error
	return { cabinClass: brandName ? fareBrandName(brandName) : name, letter }
}

export function getCabinClassName(cabinClass: Segment['cabin_class']): CabinClassName {
	const className = CabinClassCodeLabels.hasOwnProperty(cabinClass)
		? // @ts-expect-error todo if you see this please remove this comment and fix the type error
			CabinClassCodeLabels[cabinClass]
		: CabinClassLabels[cabinClass]

	return className
}

export function getCabinClassNameTranslations(t: TFunction) {
	return {
		'flights.cabin-class-name.premium-first': t('flights.cabin-class-name.premium-first'),
		'flights.cabin-class-name.first': t('flights.cabin-class-name.first'),
		'flights.cabin-class-name.premium-business': t('flights.cabin-class-name.premium-business'),
		'flights.cabin-class-name.business': t('flights.cabin-class-name.business'),
		'flights.cabin-class-name.premium-economy': t('flights.cabin-class-name.premium-economy'),
		'flights.cabin-class-name.economy': t('flights.cabin-class-name.economy'),
		'flights.cabin-class-name.first-class-train': t('flights.cabin-class-name.first-class-train'),
		'flights.cabin-class-name.second-class-train': t('flights.cabin-class-name.second-class-train'),
	}
}

export function fareBrandName(fareName: string) {
	return capitalize(fareName.toLocaleLowerCase())
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function minMax<T = any>(arr: T[], key?: keyof T | ((item: T) => number)): [number, number] {
	let min = 0
	let max = 0
	arr.forEach((item) => {
		const value = (() => {
			if (typeof key === 'string') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const _item: any = item
				return _item?.hasOwnProperty(key) ? item[key] : item
			} else if (typeof key === 'function') {
				return key(item)
			}
			return item
		})()

		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		if ((!min && value) || (value && value! < min)) {
			min = value! as number
		}

		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		if ((!max && value) || (value && value! > max)) {
			max = value! as number
		}
	})
	return [min!, max!]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function minMaxItem<T = any>(arr: T[], key?: keyof T | ((item: T) => number)) {
	let minItem: T | null = null
	let maxItem: T | null = null
	let min = 0
	let max = 0
	arr.forEach((item) => {
		const value = (() => {
			if (typeof key === 'string') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const _item: any = item
				return _item?.hasOwnProperty(key) ? item[key] : item
			} else if (typeof key === 'function') {
				return key(item)
			}
			return item
		})()

		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		if ((!min && value) || (value && value! < min)) {
			min = value! as number
			minItem = item
		}

		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		if ((!max && value) || (value && value! > max)) {
			max = value! as number
			maxItem = item
		}
	})

	return [minItem!, maxItem!]
}

export type HotelThumbResolutions = '64x64' | '75x75' | '669x414' | '210x144'

export const HotelThumbSizeMap: Record<HotelThumbResolutions, string> = {
	'64x64': '64x64',
	'75x75': '75x75',
	'669x414': '669x414',
	'210x144': '210x144',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getKeyAsReturnValue(obj: any, key?: string) {
	if (!key) {
		return obj
	}

	if (!obj) {
		return
	}

	if (obj.hasOwnProperty(key)) {
		if (typeof obj[key] === 'function') {
			return obj[key]()
		}

		return obj[key]
	}

	return
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unionArrays<T = any>(arr1: T[] = [], arr2: T[] = [], key?: keyof T): T[] {
	return [...arr1, ...arr2].reduce((unioned, item) => {
		if (
			unioned.map((u) => getKeyAsReturnValue(u, key as string)).indexOf(getKeyAsReturnValue(item, key as string)) === -1
		) {
			unioned.push(item)
		}
		return unioned
	}, [] as T[])
}

/** @deprecated Use `_.uniq` and `_.uniqBy` */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unique<T = any, R = any>(arr: T[] = [], comp?: Callback<R, T>) {
	const compFn = comp || ((a: T) => a)
	const cache: Array<ReturnType<typeof compFn>> = []
	const out: T[] = []
	for (const i of arr) {
		const v = compFn(i)
		if (!cache.includes(v)) {
			out.push(i)
			cache.push(v)
		}
	}
	return out
}

export function parseQueryString<K extends string = string>(text: string): Record<K, string> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const obj: Record<K, string> = {} as any
	;(text.startsWith('#') || text.startsWith('?') ? text.slice(1) : text).split('&').forEach((kv) => {
		const [key, value] = splitOnce(kv, '=')
		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		obj[key] = value ? decodeURIComponent(value) : 'true'
	})

	return obj
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function dumpQueryString(obj: Record<string, any>) {
	let str = ''
	for (const key of Object.keys(obj)) {
		if (key && obj.hasOwnProperty(key) && obj[key] !== undefined) {
			str += `&${key}=${encodeURIComponent(obj[key])}`
		}
	}
	return str.slice(1)
}

export function getNightCount(from: moment.MomentInput, to: moment.MomentInput) {
	return moment(to).startOf('day').diff(moment(from).startOf('day'), 'days')
}

export function isDaysPeriodFromTodaytoDate(days: number, endDate: moment.MomentInput) {
	return getNightCount(moment().startOf('day').toDate(), endDate) === days
}

const DEFAULT_DAYS_QUANTITY = 1

export const getCarNightCount = (from: moment.MomentInput, to: moment.MomentInput) =>
	getNightCount(from, to) || DEFAULT_DAYS_QUANTITY

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const paramsSerializer = (prms: any) => {
	return stringify(prms, { arrayFormat: 'repeat' })
}

export const validateEmail = (email: string) => {
	// eslint-disable-next-line no-useless-escape
	const re =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	return re.test(String(email).toLocaleLowerCase())
}

type Directions = 'top' | 'bottom' | 'left' | 'right'

export type AttachDirection = {
	[key in Directions]?: boolean
}

export interface Coords<T = number | string> {
	x: T
	y: T
}

export class Mathf {
	public static clamp(value: number, min: number, max: number) {
		return Math.min(Math.max(value, min), max)
	}
	public static clamp01(value: number) {
		return this.clamp(value, 0, 1)
	}
	public static lerp(value: number, min: number, max: number) {
		return this.clamp01(value) * (max - min) + min
	}
	public static inverseLerp(value: number, min: number, max: number) {
		return (value - min) / (max - min)
	}
}

/** Method decorator to memoize the arguments and result */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function memoized<A = any>(isEqual?: ComparisonFn<A>): MethodDecorator {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return function (target: any, propertyKey: string | symbol, descriptor: PropertyDescriptor) {
		return {
			configurable: true,
			enumerable: descriptor.enumerable,
			get: function getter() {
				Object.defineProperty(this, propertyKey, {
					configurable: true,
					enumerable: descriptor.enumerable,
					value: memoizeOne(descriptor.value.bind(this), isEqual),
				})
				// @ts-expect-error todo if you see this please remove this comment and fix the type error
				return this[propertyKey]
			},
		}
	}
}

/** Sorts by each element in the `fns` array, left to right, breaking on the first non-equal result.
 * @param fns Array of sorting functions, same as the input for `Array.prototype.sort`
 * @returns the first comparison result that isn't equal, or the last comparison if all before are equal.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function multipleSort<T = any>(fns: Array<SortingFn<T> | undefined | null>) {
	return (a: T, b: T) => {
		let r = 0
		for (const fn of fns.filter(Boolean)) {
			r = fn!(a, b)
			if (r !== 0) {
				return r
			}
		}

		return r
	}
}

/**
 * Predicate for getting a [Boolean] out of a wider range of value types.
 * Regular boolean conversion is checked first, and if that is true, also validates against common "falsy" values.
 * @param obj any object to be compared
 * @returns [Boolean]
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isBoolPredicate(obj: any): boolean {
	return Boolean(obj) && !['0', 'f', 'off', 'false', 'null', 'undefined'].includes(String(obj).toLocaleLowerCase())
}

/** Method decorator to debounce the call with a delay */
export function debounced(ms: number): MethodDecorator {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return function (target: any, propertyKey: string | symbol, descriptor: PropertyDescriptor) {
		return {
			configurable: true,
			enumerable: descriptor.enumerable,
			get: function getter() {
				Object.defineProperty(this, propertyKey, {
					configurable: true,
					enumerable: descriptor.enumerable,
					value: debounce(descriptor.value.bind(this), ms).debouncedCb,
				})
				// @ts-expect-error todo if you see this please remove this comment and fix the type error
				return this[propertyKey]
			},
		}
	}
}

export function existsInDOMParents(current: HTMLElement, target: HTMLElement): boolean {
	if (current === target) {
		return true
	}

	if (!current?.parentElement) {
		return false
	}

	return existsInDOMParents(current.parentElement!, target)
}

export function clampRange(range: [number, number] | undefined, min: number, max: number): [number, number] {
	return range ? [clamp(range[0] ?? min, min, max), clamp(range[1] ?? max, min, max)] : [min, max]
}

export interface RangeLabelConfig {
	t: TFunction
	beforeBelow?: string
	beforeAbove?: string
	afterBelow?: string
	afterAbove?: string
	showAll?: boolean
}

export function rangeLabel(
	vals: number[],
	min: number,
	max: number,
	defaultString: string,
	mapItem: (i: number) => string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	config: RangeLabelConfig = { t: noop as any },
) {
	const aboveMin = vals[0] > min
	const belowMax = vals[1] < max
	const val0 = mapItem(vals[0])
	const val1 = mapItem(vals[1])
	const priceRange = `${val0} - ${val1}`

	return aboveMin && belowMax
		? priceRange
		: aboveMin
			? `${config.beforeAbove ?? config.t('range-labels.above')} ${val0} ${config.afterAbove ?? ''}`.trim()
			: belowMax
				? `${config.beforeBelow ?? config.t('range-labels.below')} ${val1} ${config.afterBelow ?? ''}`.trim()
				: !config.showAll
					? defaultString
					: priceRange
}

interface EventStopperParams<E extends EventStopperEvent> {
	stopPropagation?: boolean
	preventDefault?: boolean
	condition?(e: E): boolean
}

type EventStopperEvent = React.SyntheticEvent
type EventStopperCb<E extends EventStopperEvent> = Callback<void, E>
type EventStopperCbOrParams<E extends EventStopperEvent> = EventStopperCb<E> | EventStopperParams<E>
type EventStopper<E extends EventStopperEvent> = (e: E) => void
type EventStopperCreateFn = <E extends EventStopperEvent>(
	paramsOrCb?: EventStopperCbOrParams<E>,
	cb?: EventStopperCb<E>,
) => EventStopper<E>

interface EventStopperFn {
	stopPropagation: EventStopperCreateFn
	preventDefault: EventStopperCreateFn;
	<E extends EventStopperEvent>(paramsOrCb?: EventStopperCbOrParams<E>, cb?: EventStopperCb<E>): EventStopper<E>
	<E extends EventStopperEvent>(cb?: EventStopperCb<E>): EventStopper<E>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isEventStoppedParams<T extends EventStopperEvent>(params: any): params is EventStopperParams<T> {
	return typeof params === 'object'
}

function createEventStopper(
	defaultParams: EventStopperParams<EventStopperEvent> = { preventDefault: true, stopPropagation: true },
): EventStopperCreateFn {
	return <E extends EventStopperEvent>(
		paramsOrCb?: EventStopperCbOrParams<E>,
		cb?: EventStopperCb<E>,
	): EventStopper<E> => {
		let params: EventStopperParams<E>
		let callback: EventStopperCb<E> | undefined

		if (isEventStoppedParams(paramsOrCb)) {
			params = paramsOrCb
			callback = cb
		} else if (isEventStoppedParams(cb)) {
			params = cb
		} else {
			callback = paramsOrCb
			params = {}
		}

		const { stopPropagation = true, preventDefault = true, condition } = { ...defaultParams, ...params }

		return (e: E) => {
			if (e) {
				if (!condition || condition(e)) {
					if (stopPropagation) {
						e.stopPropagation()
					}
					if (preventDefault) {
						e.preventDefault()
					}
				}
				e?.persist()
			}
			if (callback) {
				callback(e)
			}
		}
	}
}

export const eventStopper: EventStopperFn = Object.assign(createEventStopper(), {
	stopPropagation: createEventStopper({ preventDefault: false, stopPropagation: true }),
	preventDefault: createEventStopper({ preventDefault: true, stopPropagation: false }),
})

export function handleIOSInput() {
	if (isIOS) {
		scrollTo({ y: 0 })
	}
}

// eslint-disable-next-line no-restricted-syntax
export enum Key {
	Enter = 'Enter',
	Up = 'ArrowUp',
	Down = 'ArrowDown',
	Tab = 'Tab',
	Space = ' ',
	Escape = 'Escape',
}

interface ElementMatcherOptions<R extends HTMLElement = HTMLElement> {
	selector?: string
	match?(element: R): boolean
}

function elementMatcher<R extends HTMLElement = HTMLElement>(element: R, options: ElementMatcherOptions<R>): boolean {
	if (!element) {
		console.warn('No element specified for `elementMatcher`', { options })
		return false
	}
	if (options.selector) {
		return element.matches(options.selector)
	}

	if (options.match) {
		return options.match(element)
	}

	throw new Error('One of: [`selector` | `match`] must be specified')
}

export function getClosestElement<R extends HTMLElement = HTMLElement, T extends HTMLElement = HTMLElement>(
	el: T | null,
	options: ElementMatcherOptions<T>,
): R | null {
	if (!el) {
		return null
	}
	if (elementMatcher(el, options)) {
		return el as unknown as R
	}
	return getClosestElement(el.parentElement!, options)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function passArgsIfExistsCb<T extends Function>(cb?: T, ...args: any[]) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return (...moreArgs: any[]) => {
		cb?.(...args, ...moreArgs)
	}
}

export function autoCompleteOff(): string {
	return Math.round(Math.random() * 1000000).toString()
}

export function triggerInputChangeEvent(input: HTMLInputElement, newValue: string) {
	const oldValue = input.value
	// eslint-disable-next-line no-param-reassign
	input.value = newValue

	const ev = simulateEvent('change', { bubbles: true, cancelable: true })
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const tracker = (input as any)._valueTracker
	if (tracker) {
		tracker.setValue(oldValue)
	}
	input.dispatchEvent(ev)
}

function simulateEvent(type: string, init?: EventInit) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const ev: any = new Event(type, init)
	ev.simulated = true
	return ev
}

export function randomBetween(min: number, max: number): number
export function randomBetween(max: number): number
export function randomBetween(min: number, max?: number): number {
	return max === undefined ? Math.floor(Math.random() * min) : Math.floor(Math.random() * max) + min
}

export function isNotNil<T>(value: T): value is Exclude<T, null | undefined> {
	return !isNil(value)
}

export const isFlightsAndRailsModeActive = (mode: ProductType | null) => {
	if (mode === null) {
		return false
	}
	return [ProductType.Flights, ProductType.Rails].includes(mode)
}

export const removeEmpty = <T extends object>(rawObject: T): PartialDeep<T> => {
	const object = { ...rawObject }
	return Object.fromEntries(
		Object.entries(object)
			.filter(([_, v]) => v !== null && v !== undefined)
			.map(([k, v]) => [k, v === Object(v) ? removeEmpty(v) : v]),
	) as PartialDeep<T>
}

const getConstraintsByCountryCode = (t: TFunction): Record<string, Partial<ValidationFields>> => ({
	DE: {
		postal_code: {
			regExp: /^\d{5}$/,
			validationMessage: t('onboarding.validation-messages.postal-code-DE'),
		},
		tax_number: {
			regExp: /^DE\d{9}$/i,
			validationMessage: t('onboarding.validation-messages.tax-number-DE'),
		},
	},
	US: {
		postal_code: {
			regExp: /^\d{5}?$/,
			validationMessage: t('onboarding.validation-messages.postal-code-US'),
		},
		tax_number: {
			regExp: /^\d{2}-\d{7}$/,
			validationMessage: t('onboarding.validation-messages.tax-number-US'),
		},
	},

	GB: {
		tax_number: {
			regExp: /^GB\d{9}$/i,
			validationMessage: t('onboarding.validation-messages.tax-number-UK'),
		},
	},

	FR: {
		tax_number: {
			regExp: /^FR[A-Z0-9]{11}$/i,
			validationMessage: t('onboarding.validation-messages.tax-number-FR'),
		},
	},

	NL: {
		postal_code: {
			regExp: /^\d{4}\s[A-Za-z]{2}$/,
			validationMessage: t('Postal Code should be in format 1234 NL'),
		},
		tax_number: {
			regExp: /^NL\d{9}B\d{2}$/i,
			validationMessage: t('onboarding.validation-messages.tax-number-NL'),
		},
	},

	BE: {
		tax_number: {
			regExp: /^BE\d{10}$/i,
			validationMessage: t('onboarding.validation-messages.tax-number-BE'),
		},
	},
})

const isDependentFieldValid = (
	value: Maybe<string>,
	countryCode: Maybe<string>,
	validationFieldName: ValidationFieldName,
	t: TFunction,
) => {
	if (!value || !countryCode) {
		return true
	}

	const constrainsByCountryCode = getConstraintsByCountryCode(t)
	const validationDependentField = constrainsByCountryCode[countryCode]?.[validationFieldName]

	return !validationDependentField || value.match(validationDependentField.regExp)
		? true
		: validationDependentField.validationMessage
}

export const isPostalCodeValid = (value: Maybe<string>, countryCode: Maybe<string>, t: TFunction) => {
	return isDependentFieldValid(value, countryCode, validationFieldName.postal_code, t)
}

export const isTaxNumberValid = (value: string | null, countryCode: string, t: TFunction) => {
	return isDependentFieldValid(value, countryCode, validationFieldName.tax_number, t)
}

export function mergeMaps<K, V>(...args: Map<K, V>[]): Map<K, V> {
	const entries = args.reduce<[K, V][]>((entriesArray, currentArg) => [...entriesArray, ...currentArg.entries()], [])
	return new Map(entries)
}

export function sortMap<K, V>(map: Map<K, V>, compareFn: ((a: [K, V], b: [K, V]) => number) | undefined): Map<K, V> {
	return new Map([...map.entries()].sort(compareFn))
}

export const emptyStringsToNull = (values: Record<string, string | null>) => {
	return [...Object.keys(values)].reduce((result, key) => {
		return {
			...result,
			[key]: values[key]?.trim() === '' ? null : values[key],
		}
	}, {})
}

export const formatPrice = (price: number, currency: UserCurrency) => {
	return getPriceLabel({ currency, price, keepZeroes: true })
}

export function shallowCopy<T>(value: T) {
	if (!value) {
		return value
	}

	return { ...value }
}

export const isTaxNumberRequired = (countryCode?: string, t?: TFunction): boolean => {
	if (!countryCode) {
		return false
	}

	const defaultT: TFunction = ((_: string, fallback?: string) => fallback ?? '') as TFunction
	const constraintsByCountryCode = getConstraintsByCountryCode(t ?? defaultT)
	return !!constraintsByCountryCode[countryCode]?.tax_number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function flattenObject(obj: any) {
	const toReturn = {}

	for (const i in obj) {
		if (!obj.hasOwnProperty(i)) {
			continue
		}

		if (
			typeof obj[i] === 'object' &&
			obj[i] !== null &&
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(!Array.isArray(obj[i]) || obj[i].some((item: any) => typeof item !== 'string'))
		) {
			const flatObject = flattenObject(obj[i])
			for (const x in flatObject) {
				if (!flatObject.hasOwnProperty(x)) {
					continue
				}

				// @ts-expect-error todo if you see this please remove this comment and fix the type error
				toReturn[i + '.' + x] = flatObject[x]
			}
		} else {
			// @ts-expect-error todo if you see this please remove this comment and fix the type error
			toReturn[i] = obj[i]
		}
	}
	return toReturn
}

export function narrow<T, R extends T>(value: T, constraint: (value: T) => value is R): R | undefined
export function narrow<T, R extends T, O>(value: T, constraint: (value: T) => value is R, otherwise: O): R | O
export function narrow<T, R extends T, O>(
	value: T,
	constraint: (value: T) => value is R,
	otherwise?: O,
): R | O | undefined {
	return constraint(value) ? value : otherwise
}

export const normalizeTerms = <T extends { description?: string | null }>(terms?: T[] | null): T[] =>
	terms
		?.filter((term) => Boolean(term?.description?.trim()))
		.map((term) => ({
			...term,
			description: term.description!.replace(/\s*\n\s*/g, '\n').trim(),
		})) ?? []
